import type { ClientTool } from '../hooks/useClientTools';
import type { GraphQLClient } from './personaTools';
import {
  ListMcpServersDocument,
  CreateSecretDocument,
  EnableMcpServerDocument,
} from '@matrix-forge/graphql-client';

export const MCP_SETUP_TOOL_NAMES = ['setup_mcp_server'] as const;

/** Creates MCP server setup client tools backed by GraphQL. */
export function createMcpSetupTools(client: GraphQLClient): ClientTool[] {
  return [createSetupMcpServerTool(client)];
}

/**
 * Extract credential field names that a server needs from its authSchema,
 * headersTemplate, and authRequirements. Returns the unique set of field names.
 */
function extractRequiredAuthFields(server: any): string[] {
  const fields = new Set<string>();

  // From authSchema properties
  const properties = server.authSchema?.properties;
  if (properties && typeof properties === 'object') {
    for (const key of Object.keys(properties)) {
      fields.add(key);
    }
  }

  // From headersTemplate {{secrets.xxx}} patterns
  const headersTemplate = server.headersTemplate;
  if (headersTemplate && typeof headersTemplate === 'object') {
    for (const template of Object.values(headersTemplate)) {
      if (typeof template !== 'string') continue;
      const matches = template.matchAll(/\{\{secrets\.(\w+)\}\}/g);
      for (const match of matches) {
        fields.add(match[1]);
      }
    }
  }

  // From authRequirements (Construct-sourced servers)
  const authRequirements = server.authRequirements;
  if (Array.isArray(authRequirements)) {
    for (const req of authRequirements) {
      if (req?.sourceHeader) {
        fields.add(req.sourceHeader);
      }
    }
  }

  return Array.from(fields);
}

function createSetupMcpServerTool(client: GraphQLClient): ClientTool {
  return {
    name: 'setup_mcp_server',
    description: `Enable/install an MCP server for the tenant. IMPORTANT: You MUST pass
the "credentials" parameter with the values collected from request_user_input.
Do NOT call this tool without credentials if the server has authFields.

Flow:
1. Call list_mcp_catalog(search: "name") to find the server
2. Use request_user_input to collect credentials from the user
3. Call this tool with templateId AND credentials (the collected values)

The credentials parameter is a map of field names to raw values, e.g.:
{"splunk_token": "eyJ...", "splunk_url": "https://...", "X-Splunk-Token": "eyJ..."}
Field names should match the authFields returned by list_mcp_catalog.`,
    parameters: {
      type: 'object',
      properties: {
        templateId: {
          type: 'string',
          description: 'The MCP server template ID (from list_mcp_catalog).',
        },
        credentials: {
          type: 'object',
          description:
            'REQUIRED if the server needs auth. Map of field name to raw value from the user. You MUST pass the values collected via request_user_input here. Field names must match those from authFields in list_mcp_catalog.',
        },
        url: {
          type: 'string',
          description: 'Custom server URL. Only needed if overriding the default template URL.',
        },
        consentMode: {
          type: 'string',
          enum: ['manual', 'approve_always', 'deny_always'],
          description:
            'Tool consent mode. "manual" (default) requires user approval for each tool call, "approve_always" auto-approves, "deny_always" auto-denies.',
        },
      },
      required: ['templateId'],
    },
    handler: async (args: {
      templateId: string;
      credentials?: Record<string, string>;
      url?: string;
      consentMode?: string;
    }) => {
      if (!args.templateId) {
        return { error: { message: 'templateId is required.' } };
      }

      try {
        // Step 1: Fetch server details to get authSchema, headersTemplate, authRequirements
        const { data: serversData } = await client.query({
          query: ListMcpServersDocument,
          fetchPolicy: 'network-only',
        });

        const server = (serversData?.listMcpServers ?? []).find(
          (s: any) => s.templateId === args.templateId
        );

        if (!server) {
          return {
            error: {
              message: `MCP server template "${args.templateId}" not found. Use list_mcp_catalog to see available servers.`,
            },
          };
        }

        if (server.comingSoon) {
          return {
            error: {
              message: `MCP server "${server.templateName}" is marked as coming soon and cannot be installed yet.`,
            },
          };
        }

        // Step 2: Check if credentials are required but missing
        const requiredFields = extractRequiredAuthFields(server);
        const credentials = args.credentials ?? {};
        const hasCredentials = Object.keys(credentials).length > 0;

        if (requiredFields.length > 0 && !hasCredentials) {
          return {
            error: {
              message: `Server "${server.templateName}" requires credentials but none were provided. Use request_user_input to collect these fields from the user, then call setup_mcp_server again with the collected values in the "credentials" parameter.`,
              requiredFields,
              hint: 'Call request_user_input first to collect credentials, then pass them as the "credentials" parameter to this tool.',
            },
          };
        }

        // Step 3: Determine the server type and process credentials accordingly
        const isConstruct = server.source === 'construct';
        const authRequirements: any[] = server.authRequirements ?? [];
        const authSchema = server.authSchema;
        const headersTemplate: Record<string, string> = server.headersTemplate ?? {};

        const secretsMap: Record<string, string> = {};
        const headers: Record<string, string> = {};

        if (hasCredentials && isConstruct && authRequirements.length > 0) {
          // Construct-sourced servers: credentials are keyed by sourceHeader name
          // (e.g. "X-Splunk-Token"). This matches the UI's handleConstructInstall logic:
          // - Sensitive fields (token/password): create a secret, header = {{secrets.key}}
          // - Non-sensitive fields (base URL): header = raw value (no secret)
          const isSensitiveField = (key: string) =>
            /token|password/i.test(key);

          const slug = server.sourcePluginSlug ?? args.templateId;

          for (const req of authRequirements) {
            const headerName = req.sourceHeader;
            if (!headerName) continue;

            const rawValue = credentials[headerName];
            if (!rawValue || !rawValue.trim()) continue;

            // Derive secret key the same way the UI does:
            // e.g. "X-Splunk-Token" → "splunk_token"
            const secretKey = headerName
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '_')
              .replace(/^x_/, '');

            if (isSensitiveField(headerName)) {
              // Create a secret for sensitive fields (token, password)
              const secretName = `mcp-${slug}-${secretKey}`;

              const { data: secretData } = await client.mutate({
                mutation: CreateSecretDocument,
                variables: {
                  input: {
                    name: secretName,
                    type: 'TOKEN',
                    value: rawValue.trim(),
                  },
                },
              });

              const secretId = secretData?.createSecret?.id;
              if (!secretId) {
                return {
                  error: {
                    message: `Failed to create secret for field "${headerName}".`,
                  },
                };
              }

              secretsMap[secretKey] = secretId;
              headers[headerName] = `{{secrets.${secretKey}}}`;
            } else {
              // Non-sensitive fields go directly as plain header values
              headers[headerName] = rawValue.trim();
            }
          }
        } else if (hasCredentials) {
          // Standalone/platform servers: credentials keyed by authSchema property names
          const properties = authSchema?.properties ?? {};

          for (const [fieldName, rawValue] of Object.entries(credentials)) {
            const fieldSchema = properties[fieldName];

            // Determine if this field is a secret (token/password) or a literal config value.
            const hasSchema = fieldSchema != null;
            const isSecretBySchema =
              fieldSchema?.secret_type === 'token' ||
              fieldSchema?.secret_type === 'basic_auth' ||
              fieldSchema?.format === 'password';
            const isSecretByName = /token|key|secret|password|auth/i.test(fieldName);

            const isSecretField = hasSchema ? isSecretBySchema : isSecretByName;

            if (isSecretField) {
              const secretName = `mcp_${args.templateId}_${fieldName}`;
              const secretType =
                fieldSchema?.secret_type === 'basic_auth' ? 'BASIC_AUTH' : 'TOKEN';

              const { data: secretData } = await client.mutate({
                mutation: CreateSecretDocument,
                variables: {
                  input: {
                    name: secretName,
                    type: secretType,
                    value: rawValue,
                  },
                },
              });

              const secretId = secretData?.createSecret?.id;
              if (!secretId) {
                return {
                  error: {
                    message: `Failed to create secret for field "${fieldName}".`,
                  },
                };
              }

              secretsMap[fieldName] = secretId;
            } else {
              // Non-secret field (e.g., enum config value, URL) — store as literal
              secretsMap[fieldName] = rawValue;
            }
          }

          // Step 4: Pass headers template through as-is — the backend resolves
          // {{secrets.fieldName}} references using the secrets map we provide.
          for (const [headerName, template] of Object.entries(headersTemplate)) {
            if (typeof template === 'string') {
              headers[headerName] = template;
            }
          }
        }

        // Step 5: Call EnableMcpServer
        // secrets and headers must be JSON-stringified for the Json scalar
        const enableInput: Record<string, any> = {
          templateId: args.templateId,
        };

        if (Object.keys(secretsMap).length > 0) {
          enableInput.secrets = JSON.stringify(secretsMap);
        }
        if (Object.keys(headers).length > 0) {
          enableInput.headers = JSON.stringify(headers);
        }
        if (args.url) {
          enableInput.url = args.url;
        }
        if (args.consentMode) {
          enableInput.consentMode = args.consentMode;
        }

        const { data: enableData } = await client.mutate({
          mutation: EnableMcpServerDocument,
          variables: { input: enableInput },
          refetchQueries: [{ query: ListMcpServersDocument }],
        });

        const result = enableData?.enableMcpServer;
        if (!result) {
          return { error: { message: 'EnableMcpServer mutation returned no data.' } };
        }

        if (!result.success) {
          return {
            error: {
              message: result.message || 'Failed to enable MCP server.',
              validationStatus: result.validationStatus,
              validationMessage: result.validationMessage,
            },
          };
        }

        return {
          result: {
            success: true,
            message: `MCP server "${server.templateName}" enabled successfully.`,
            templateId: args.templateId,
            name: server.templateName,
            toolCount: result.toolCount ?? 0,
            validationStatus: result.validationStatus,
            validationMessage: result.validationMessage,
          },
        };
      } catch (err) {
        return {
          error: {
            message: err instanceof Error ? err.message : 'Failed to setup MCP server',
          },
        };
      }
    },
  };
}
